import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Autocomplete, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import {
  FormTableAnalyticParameter,
  TABLE_ANALYTIC_SCALE_OPTION_VALUE,
  TABLE_ANALYTICS_SCALE_OPTIONS,
  tableAnalyticParameterValidatonSchema,
} from 'components/ButtonComponent/ButtonTableAnalytic';
import { useFormik } from 'formik';
import { useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { useGetTableAnalyticOptions } from 'services/v1/SystemTenant/AROKMS/TableAnalyticService';
import { ControllerNavigation, ControllerNavigationDirection } from 'pages/Tenant/AroDashboard/ControllerNavigation';
import { RowenaContent } from 'pages/Tenant/AroDashboard/RowenaContent';
import { useGetMyPersonalization, useGetMyViewDetails } from 'services/v1/Tenant/ConfigurationService';
import { getCurrentMyViewIndex, getNextMyViewItem, getPreviousMyViewItem } from 'utils/MyViewUtils';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';
import { PAGE_REF_NAVIGATION } from 'utils/Animation';

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '4px 8px',
    fontSize: '13px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '13px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '13px',
  },
};

const textInputStyles = {
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '13px',
  },
};

const radioButtonLabelStyle = {
  '& .MuiFormControlLabel-label': {
    fontSize: '13px',
    color: '#667085',
  },
};

const radioButtonStyle = {
  color: '#98A2AE',
  padding: '4px',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&.Mui-checked': {
    color: '#42BB93',
  },
};

const emptyFormikValues: FormTableAnalyticParameter = {
  rowColumn: null,
  columnColumn: null,
  dataColumn: {
    label: 'Count',
    value: 'COUNT',
  },
  dataDecimals: '0',
  scale: 'NONE',
};

interface ModalParameterControllerProps {
  initialValues?: FormTableAnalyticParameter;
  onChange: (values: FormTableAnalyticParameter) => void;
  loading?: boolean;
}

export function ModalParameterController(props: ModalParameterControllerProps) {
  const { initialValues, onChange, loading = false } = props;
  const navigate = useNavigate();
  const { subjectId } = useParams<{ subjectId: string }>();
  const [searchParams] = useSearchParams();
  const myViewId = searchParams.get('myViewId');

  const { data: allMyViewsData } = useGetMyPersonalization({ enabled: true });
  const { data: myViewDetails } = useGetMyViewDetails(myViewId);
  const { data: subjectDetails } = useGetSubjectDetails(subjectId);

  const { data: tableAnalyticOptions } = useGetTableAnalyticOptions({
    enable: true,
    subjectId: subjectDetails?.data?.id?.toString(),
  });

  const formik = useFormik<FormTableAnalyticParameter>({
    initialValues: initialValues || emptyFormikValues,
    validationSchema: tableAnalyticParameterValidatonSchema,
    enableReinitialize: true,
    onSubmit: (values, formikHelper) => {
      onChange({
        ...values,
        scale: values.scale === 'NONE' ? undefined : values.scale,
      });
      formikHelper.resetForm();
    },
  });

  const handleOnChange = (values: FormTableAnalyticParameter) => {
    onChange({
      ...values,
      scale: values.scale === 'NONE' ? undefined : values.scale,
    });
  };

  const rowOptions = useMemo(() => {
    if (tableAnalyticOptions?.data?.rowOptions) {
      return tableAnalyticOptions?.data?.rowOptions.filter(
        (option) => option.value !== formik.values.columnColumn?.value,
      );
    }
    return [];
  }, [tableAnalyticOptions?.data?.rowOptions, formik.values.columnColumn]);

  const columnOptions = useMemo(() => {
    if (tableAnalyticOptions?.data?.columnOptions) {
      return tableAnalyticOptions?.data?.columnOptions.filter(
        (option) => option.value !== formik.values.rowColumn?.value,
      );
    }
    return [];
  }, [tableAnalyticOptions?.data?.columnOptions, formik.values.rowColumn]);

  const pageViewList = useMemo(() => {
    if (allMyViewsData?.data) {
      const selectedDashboardId = myViewDetails?.data.dashboardId;
      const selectedDashboard =
        allMyViewsData.data.dashboards.find((dashboardItem) => dashboardItem.id === selectedDashboardId) || null;

      if (selectedDashboard) {
        return selectedDashboard.myViews;
      }
    }
    return [];
  }, [allMyViewsData?.data, myViewDetails?.data]);

  const currentPageIndex = useMemo(() => {
    if (myViewId == null) return 0;
    return getCurrentMyViewIndex(pageViewList, myViewId);
  }, [myViewId, pageViewList]);

  const nextPageViewItem = useMemo(() => {
    return getNextMyViewItem(pageViewList, currentPageIndex);
  }, [currentPageIndex, pageViewList]);

  const previousPageViewItem = useMemo(() => {
    return getPreviousMyViewItem(pageViewList, currentPageIndex);
  }, [currentPageIndex, pageViewList]);

  const controllerNaviations = useMemo(() => {
    const controller = [];

    if (pageViewList?.length > 1) {
      const currentPageIndex = pageViewList.findIndex((item) => item.viewId === myViewId);
      if (currentPageIndex > 0) controller.push(ControllerNavigationDirection.LEFT);
      if (currentPageIndex < pageViewList.length - 1) controller.push(ControllerNavigationDirection.RIGHT);
    }
    return controller;
  }, [pageViewList, myViewId]);

  const navigationControllerTooltips = useMemo(() => {
    const defaultToolTips: {
      [key: string]: string;
    } = {};

    if (pageViewList.length > 1) {
      const currentPageIndex = pageViewList.findIndex((item) => item.viewId === myViewId);
      if (currentPageIndex > -1) {
        if (currentPageIndex > 0) {
          defaultToolTips[ControllerNavigationDirection.LEFT] = `Show ${pageViewList[currentPageIndex - 1].title}`;
        }
        if (currentPageIndex < pageViewList.length - 1) {
          defaultToolTips[ControllerNavigationDirection.RIGHT] = `Show ${pageViewList[currentPageIndex + 1].title}`;
        }
      }
    }

    return defaultToolTips;
  }, [pageViewList, myViewId]);

  const handleOnDashboardNavigate = (direction: ControllerNavigationDirection) => {
    switch (direction) {
      case ControllerNavigationDirection.LEFT:
        const previousMyViewItemURL = getQuickAccessNavigateURL(
          previousPageViewItem,
          undefined,
          PAGE_REF_NAVIGATION.LEFT,
        );
        previousMyViewItemURL && navigate(previousMyViewItemURL);
        break;
      case ControllerNavigationDirection.RIGHT:
        const nextMyViewItemURL = getQuickAccessNavigateURL(nextPageViewItem, undefined, PAGE_REF_NAVIGATION.RIGHT);
        nextMyViewItemURL && navigate(nextMyViewItemURL);
        break;

      default:
        break;
    }
  };

  return (
    <Stack
      width='100%'
      gap={2}
      sx={{
        p: 2,
        borderRadius: '10px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
      }}
    >
      {/* Dimension Selectors */}
      <Stack direction='row' gap={2} justifyContent='space-between' alignItems='center'>
        <Stack width='100%' flexDirection='row' gap={2}>
          <Stack flex={1} minWidth={0}>
            <Autocomplete
              options={columnOptions}
              value={formik.values.columnColumn}
              sx={autocompleteStyles}
              disabled={loading}
              onChange={(_event, newValue) => {
                formik.setFieldValue('columnColumn', newValue);
                handleOnChange({ ...formik.values, columnColumn: newValue });
              }}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label='Column' placeholder='Select attribute...' />}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Autocomplete
              options={rowOptions}
              value={formik.values.rowColumn}
              disabled={loading}
              sx={autocompleteStyles}
              onChange={(_event, newValue) => {
                formik.setFieldValue('rowColumn', newValue);
                handleOnChange({ ...formik.values, rowColumn: newValue });
              }}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label='Row' placeholder='Select attribute...' />}
            />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Autocomplete
              options={tableAnalyticOptions?.data?.dataOptions || []}
              value={formik.values.dataColumn}
              disabled={loading}
              sx={autocompleteStyles}
              onChange={(_event, newValue) => {
                formik.setFieldValue('dataColumn', newValue);
                handleOnChange({ ...formik.values, dataColumn: newValue });
              }}
              clearIcon={null}
              renderInput={(params) => <TextField {...params} label='Data' placeholder='Select attribute...' />}
            />
          </Stack>
        </Stack>
        <Stack ml={1} mr={1}>
          <RowenaContent />
        </Stack>
        {controllerNaviations.length > 0 && (
          <ControllerNavigation
            enableNavigations={controllerNaviations}
            onNavigate={handleOnDashboardNavigate}
            tooltips={navigationControllerTooltips}
          />
        )}
      </Stack>

      {/* Decimals & Scale */}
      <Stack direction='row' gap={2} alignItems='center'>
        <Stack width={160}>
          <TextField
            sx={textInputStyles}
            name='dataDecimals'
            label='Data Decimals'
            disabled={loading}
            value={formik.values.dataDecimals}
            onChange={(e) => {
              if (/^[0-9]*$/.test(e.target.value)) {
                formik.handleChange(e);
                handleOnChange({ ...formik.values, dataDecimals: e.target.value });
              }
            }}
            onBlur={formik.handleBlur}
            variant='outlined'
            size='small'
            placeholder='e.g. 3'
          />
        </Stack>
        <Stack flexDirection='column'>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '12px',
              color: '#667085',
              mb: 0.25,
            }}
          >
            Scale
          </Typography>
          <RadioGroup
            value={formik.values.scale}
            onChange={(_event, value) => {
              formik.setFieldValue('scale', value);
              if (value === TABLE_ANALYTIC_SCALE_OPTION_VALUE.MILLION) {
                formik.setFieldValue('dataDecimals', '2');
                handleOnChange({ ...formik.values, scale: value, dataDecimals: '2' });
              } else {
                handleOnChange({ ...formik.values, scale: value });
              }
            }}
          >
            <Stack direction='row' spacing={0.5}>
              {TABLE_ANALYTICS_SCALE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  disabled={loading}
                  value={option.value}
                  sx={radioButtonLabelStyle}
                  control={<Radio sx={radioButtonStyle} />}
                  label={option.label}
                />
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
      </Stack>
    </Stack>
  );
}
